/**
 * ConversationThread — right panel: header + timeline + reply compose.
 *
 * Props:
 *   conversationId  {signal<number|null>}
 *   people          {array}               — for assignee dropdown
 *   onThreadUpdated {function}            — called after reply / status change
 */

import { h, Fragment } from 'preact'
import { useState, useEffect, useRef } from 'preact/hooks'
import { patch } from '@rails/request.js'
import { ConversationMessage } from './ConversationMessage.jsx'
import { ConversationNote }    from './ConversationNote.jsx'
import { ReplyCompose }        from './ReplyCompose.jsx'
import { StatusBadge, STATUS_LABELS, statusCssKey } from './StatusBadge.jsx'
import { AssigneeAvatar }      from './AssigneeAvatar.jsx'
import { avatarColor }         from './utils.js'

const STATUSES = ['open', 'inprogress', 'waiting_for_customer', 'closed']

export function ConversationThread({ conversationId, onBack, onOpenContext, onThreadUpdated }) {
  const [thread,   setThread]   = useState(null)
  const [loading,  setLoading]  = useState(false)
  const [expanded, setExpanded] = useState({})
  const timelineRef = useRef(null)

  const id = conversationId

  // Load thread when id changes
  useEffect(() => {
    if (!id) { setThread(null); return }
    setLoading(true)
    setExpanded({})
    fetch(`/admin/support/conversations/${id}.json`, {
      headers: { Accept: 'application/json' },
    })
      .then(r => r.json())
      .then(data => {
        setThread(data)
        // Auto-expand the last email item
        const items = data.thread_items || []
        const lastEmail = [...items].reverse().find(i => i.type === 'email')
        if (lastEmail) {
          const eid = lastEmail.email?.id || lastEmail.id
          setExpanded(prev => ({ ...prev, [eid]: true }))
        }
      })
      .catch(err => console.error('[ConversationThread] fetch error', err))
      .finally(() => setLoading(false))
  }, [id])

  // Scroll to top when thread loads (newest item is first)
  useEffect(() => {
    if (thread && timelineRef.current) {
      timelineRef.current.scrollTop = 0
    }
  }, [thread])

  async function handleStatusChange(status) {
    if (!id) return
    try {
      const res = await patch(`/admin/support/conversations/${id}/change_status`, {
        body: JSON.stringify({ status }),
        contentType: 'application/json',
        responseKind: 'json',
      })
      if (res.ok) {
        setThread(prev => prev
          ? { ...prev, conversation: { ...prev.conversation, status } }
          : prev
        )
        onThreadUpdated?.({ status })
      }
    } catch (err) {
      console.error('[ConversationThread] status change error', err)
    }
  }

  function handleAssigned(data) {
    setThread(prev => prev
      ? { ...prev, conversation: { ...prev.conversation, responsible: data.responsible } }
      : prev
    )
    onThreadUpdated?.(data)
  }

  function handleSent() {
    if (!id) return
    setLoading(true)
    fetch(`/admin/support/conversations/${id}.json`, { headers: { Accept: 'application/json' } })
      .then(r => r.json())
      .then(freshData => {
        setThread(freshData)
        // Auto-expand last email after reload
        const items = freshData.thread_items || []
        const lastEmail = [...items].reverse().find(i => i.type === 'email')
        if (lastEmail) {
          const eid = lastEmail.email?.id || lastEmail.id
          setExpanded(prev => ({ ...prev, [eid]: true }))
        }
      })
      .catch(err => console.error('[ConversationThread] reload error', err))
      .finally(() => setLoading(false))
    onThreadUpdated?.({})
  }

  function toggleExpand(emailId) {
    setExpanded(prev => ({ ...prev, [emailId]: !prev[emailId] }))
  }

  // ── Empty state (no conversation selected) ─────────────────────────────────
  if (!id) {
    return (
      <div class="support-thread-panel">
        <div class="support-empty">
          <i class="bi bi-envelope-open" />
          <p>Válasszon ki egy beszélgetést a listából</p>
        </div>
      </div>
    )
  }

  // ── Loading skeleton ────────────────────────────────────────────────────────
  if (loading && !thread) {
    return (
      <div class="support-thread-panel">
        <div class="support-loading thread-loading">
          <div class="spinner-border support-spinner-lg" role="status" />
          <span>Betöltés...</span>
        </div>
      </div>
    )
  }

  const conversation = thread?.conversation || {}
  const items        = [...(thread?.thread_items || [])].reverse()
  const people       = thread?.people       || []
  const statusKey    = statusCssKey(conversation.status)

  return (
    <div class="support-thread-panel">

      {/* ── Thread header ────────────────────────────────────────────── */}
      <div class="thread-header">
        {/* Mobile back button — only visible on small screens */}
        {onBack && (
          <button
            type="button"
            class="thread-back-btn d-md-none"
            onClick={onBack}
            aria-label="Vissza a listához"
          >
            <i class="bi bi-chevron-left" />
          </button>
        )}
        <div class="thread-header-info">
          <p class="thread-subject">
            {conversation.subject || '(Nincs tárgy)'}
          </p>
          <div class="thread-meta">
            {conversation.from_name}
            {conversation.mailbox_name && (
              <> &middot; {conversation.mailbox_name}</>
            )}
          </div>
        </div>

        <div class="thread-actions">
          {/* Status dropdown */}
          <div class="dropdown">
            <button
              type="button"
              class={`status-select-btn status-${statusKey} dropdown-toggle`}
              data-bs-toggle="dropdown"
              data-bs-strategy="fixed"
            >
              <span class={`status-dot status-dot-${statusKey}`} />
              {STATUS_LABELS[conversation.status] || conversation.status}
            </button>
            <ul class="dropdown-menu dropdown-menu-end shadow-sm support-dropdown">
              {STATUSES.map(s => {
                const sk = statusCssKey(s)
                return (
                  <li key={s}>
                    <a
                      class="dropdown-item d-flex align-items-center gap-2 small"
                      href="#"
                      onClick={e => { e.preventDefault(); handleStatusChange(s) }}
                    >
                      <span class={`status-dot status-dot-${sk}`} />
                      <span class={conversation.status === s ? 'fw-semibold' : ''}>
                        {STATUS_LABELS[s]}
                      </span>
                      {conversation.status === s && (
                        <i class="bi bi-check ms-auto text-primary" />
                      )}
                    </a>
                  </li>
                )
              })}
            </ul>
          </div>

          {/* Assignee avatar */}
          <AssigneeAvatar
            responsible={conversation.responsible}
            people={people}
            conversationId={id}
            onAssigned={handleAssigned}
            size={30}
          />

          {/* Context info button — mobile only */}
          {onOpenContext && (
            <button
              type="button"
              class="thread-action-btn thread-context-btn d-md-none"
              onClick={onOpenContext}
              title="Feladó adatai"
            >
              <i class="bi bi-person-lines-fill" />
            </button>
          )}
        </div>
      </div>

      {/* ── Timeline ─────────────────────────────────────────────────── */}
      <div class="thread-timeline" ref={timelineRef}>
        {loading && (
          <div class="support-loading loadmore-loading">
            <div class="spinner-border spinner-border-sm support-spinner-md" role="status" />
            <span class="spinner-sm">Frissítés...</span>
          </div>
        )}

        {items.length === 0 && !loading
          ? (
            <div class="support-empty thread-empty">
              <i class="bi bi-chat" />
              <p>Nincsenek üzenetek</p>
            </div>
          )
          : items.map((item, idx) => {
            if (item.type === 'email') {
              const emailId = item.email?.id || item.id || idx
              return (
                <ConversationMessage
                  key={`email-${emailId}`}
                  item={item}
                  isExpanded={!!expanded[emailId]}
                  onToggle={() => toggleExpand(emailId)}
                />
              )
            } else if (item.type === 'note') {
              const noteId = item.note?.id || item.id || idx
              return <ConversationNote key={`note-${noteId}`} item={item} />
            }
            return null
          })
        }
      </div>

      {/* ── Reply compose ────────────────────────────────────────────── */}
      <ReplyCompose
        conversationId={id}
        mailboxSignature={conversation.signature_html || thread?.mailbox_signature}
        onSent={handleSent}
      />

    </div>
  )
}
